import { Frame } from '@/frame';
import { Button } from '@/ui/button/button.component';
import { Checkbox } from '@/ui/checkbox/checkbox.component';
import { Loader } from '@/ui/loader/loader.component';
import { Radio } from '@/ui/radio-button/radio-button.component';
import { ContactPropertyKey } from '@/graphql/generated/types';
import { error, success } from '@/ui/toaster/toast';
import { navigate, RouteComponentProps } from '@reach/router';
import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AttributesView } from './attributes-view/attributes-view.component';
import {
  useAdminMergePersonsMutation,
  useAdminMarkNotDuplicateMutation,
  usePersonWithDuplicatesQuery,
  PersonWithDuplicatesQuery,
} from './duplicate-resolver.generated';
import styles from './duplicate-resolver.module.scss';
import { MergeState, PersonId } from './types';
import { extractCandidates } from './utils';

const MULTI_VALUE_KEYS = new Set([
  ContactPropertyKey.Email,
  ContactPropertyKey.Phone,
  ContactPropertyKey.Mobile,
]);

export type PersonDuplicateTypeBase = PersonWithDuplicatesQuery['personWithDuplicates'];

type MergeCandidate = Omit<
  PersonWithDuplicatesQuery['personWithDuplicates'],
  'duplicates' | '__typename' | 'enterprises' | 'enterpriseIds'
>;

/** Build the overrides object from the supervisor's field selections. */
function buildOverrides(
  state: MergeState,
  primaryPersonId: string,
  candidates: MergeCandidate[],
  contactPropsState: Record<string, boolean>,
) {
  const overrides: Record<string, any> = {};

  for (const [property, chosenPersonId] of Object.entries(state)) {
    if (!chosenPersonId || chosenPersonId === primaryPersonId) continue;
    const candidate = candidates.find((c) => c.id === chosenPersonId);
    if (!candidate) continue;

    switch (property) {
      case 'firstName':
        overrides.firstName = candidate.firstName;
        break;
      case 'lastName':
        overrides.lastName = candidate.lastName;
        break;
      case 'title':
        overrides.title = candidate.title;
        break;
      case 'gender':
        overrides.gender = candidate.gender;
        break;
      case 'internalNotes':
        overrides.internalNotes = candidate.internalNotes;
        break;
      case 'address':
        overrides.address = candidate.address
          ? { ...candidate.address, __typename: undefined }
          : null;
        break;
      case 'invoiceAddress':
        overrides.invoiceAddress = candidate.invoiceAddress
          ? { ...candidate.invoiceAddress, __typename: undefined }
          : null;
        break;
      case 'customerNumber':
        if (candidate.user?.__typename === 'Customer') {
          overrides.customerNumber = (candidate.user as any).customerNumber;
        }
        break;
      case 'invoiceEmail':
        if (candidate.user?.__typename === 'Customer') {
          overrides.invoiceEmail = (candidate.user as any).invoiceEmail;
        }
        break;
    }
  }

  // If any contact property value is explicitly excluded, send the final list.
  const anyExcluded = Object.values(contactPropsState).some((v) => !v);
  if (anyExcluded) {
    overrides.contactProperties = Object.entries(contactPropsState)
      .filter(([, included]) => included)
      .map(([keyValue]) => {
        const colonIdx = keyValue.indexOf(':');
        return {
          key: keyValue.substring(0, colonIdx),
          value: keyValue.substring(colonIdx + 1),
        };
      });
  }

  return overrides;
}

export const DuplicateResolver: ComponentType<
  RouteComponentProps<{ id: string }>
> = (props) => {
  const { id } = props;

  const [primaryRecordId, setPrimaryRecordId] = useState<PersonId>();
  const [activeRecordIds, setActiveRecordIds] = useState<PersonId[]>([]);
  const [mergeState, setMergeState] = useState<MergeState>({});
  const [contactPropsState, setContactPropsState] = useState<Record<string, boolean>>({});
  const [customerNumberConflict, setCustomerNumberConflict] = useState(false);

  const secondaryPersonIds = useMemo(
    () => activeRecordIds.filter((rid) => rid !== primaryRecordId),
    [activeRecordIds, primaryRecordId],
  );

  const isInitial = useRef(true);

  const { data, error: queryError } = usePersonWithDuplicatesQuery({
    variables: { id },
    skip: id === undefined,
    fetchPolicy: 'network-only',
  });

  const [adminMergePersons, { loading: merging }] = useAdminMergePersonsMutation();
  const [adminMarkNotDuplicate] = useAdminMarkNotDuplicateMutation();

  const person = data?.personWithDuplicates;
  const candidates = useMemo(() => extractCandidates(person), [person]);

  const mergePossible = activeRecordIds.length >= 2 && !!primaryRecordId;

  // Detect customerNumber conflict between the primary and any active secondary.
  const hasCustomerNumberConflict = useMemo(() => {
    if (!primaryRecordId || secondaryPersonIds.length === 0) return false;
    const primary = candidates.find((c) => c.id === primaryRecordId);
    const primaryNum =
      primary?.user?.__typename === 'Customer'
        ? (primary.user as any).customerNumber
        : null;
    if (!primaryNum) return false;
    return secondaryPersonIds.some((secondaryId) => {
      const secondary = candidates.find((c) => c.id === secondaryId);
      const secondaryNum =
        secondary?.user?.__typename === 'Customer'
          ? (secondary.user as any).customerNumber
          : null;
      return !!(secondaryNum && secondaryNum !== primaryNum);
    });
  }, [candidates, primaryRecordId, secondaryPersonIds]);

  const customerNumberResolved = !hasCustomerNumberConflict || !!mergeState['customerNumber'];

  const toggleActiveRecord = useCallback(
    (recordId: string, active: boolean) => {
      setActiveRecordIds((recordIds) =>
        active
          ? [...recordIds, recordId]
          : recordIds.filter((rid) => rid !== recordId),
      );
      if (!active && primaryRecordId === recordId) {
        setPrimaryRecordId(
          candidates.find(
            (d) => d.id !== recordId && activeRecordIds.includes(d.id),
          )?.id,
        );
      }
    },
    [candidates, primaryRecordId, activeRecordIds],
  );

  const handleStateChange = useCallback((property: string, personId: string) => {
    setMergeState((prev) => ({ ...prev, [property]: personId }));
  }, []);

  const handleContactPropToggle = useCallback(
    (key: string, value: string, checked: boolean) => {
      setContactPropsState((prev) => ({ ...prev, [`${key}:${value}`]: checked }));
    },
    [],
  );

  // Activate all records on mount.
  useEffect(() => {
    if (candidates.length > 0 && isInitial.current) {
      isInitial.current = false;
      setActiveRecordIds(candidates.map((c) => c.id));
    }
  }, [candidates]);

  // Initialize contactPropsState: collect all multi-value contact properties from all candidates.
  useEffect(() => {
    if (candidates.length === 0) return;
    setContactPropsState((prev) => {
      const next: Record<string, boolean> = {};
      for (const candidate of candidates) {
        for (const cp of candidate.contactProperties ?? []) {
          if (MULTI_VALUE_KEYS.has(cp.key as ContactPropertyKey)) {
            const k = `${cp.key}:${cp.value}`;
            next[k] = prev[k] ?? true;
          }
        }
      }
      return next;
    });
  }, [candidates]);

  useEffect(() => {
    if (person) {
      setPrimaryRecordId(person.id);
    }
  }, [person]);

  const handleMerge = useCallback(async () => {
    if (!primaryRecordId || secondaryPersonIds.length === 0) return;

    if (hasCustomerNumberConflict && !mergeState['customerNumber']) {
      setCustomerNumberConflict(true);
      error('Bitte wähle eine Kundennummer aus.');
      return;
    }

    const overrides = buildOverrides(mergeState, primaryRecordId, candidates, contactPropsState);
    const hasOverrides = Object.keys(overrides).length > 0;
    const { contactProperties, ...overridesWithoutCp } = overrides;

    try {
      for (let i = 0; i < secondaryPersonIds.length; i++) {
        const isLast = i === secondaryPersonIds.length - 1;
        // Only apply contactProperties override on the last merge so earlier
        // merges don't overwrite the accumulating contact property list.
        const callOverrides = isLast ? overrides : overridesWithoutCp;
        const hasCallOverrides = Object.keys(callOverrides).length > 0;
        await adminMergePersons({
          variables: {
            input: {
              primaryPersonId: primaryRecordId,
              secondaryPersonId: secondaryPersonIds[i],
              overrides: hasCallOverrides ? callOverrides : undefined,
            },
          },
        });
      }
      success('Die Personen wurden erfolgreich zusammengeführt.');
      const userTypeSegment =
        person.user?.__typename === 'Customer' ? 'customers' :
        person.user?.__typename === 'Teacher' ? 'teachers' :
        person.user?.__typename === 'Administrator' ? 'administrators' :
        'persons';
      navigate(`../${userTypeSegment}/${primaryRecordId}/edit`);
    } catch (e: any) {
      if (e?.message?.includes('CUSTOMER_NUMBER_CONFLICT')) {
        setCustomerNumberConflict(true);
        error('Bitte wähle eine Kundennummer aus.');
      } else {
        error('Beim Zusammenführen ist ein Fehler aufgetreten.');
      }
    }
  }, [
    primaryRecordId,
    secondaryPersonIds,
    mergeState,
    candidates,
    contactPropsState,
    hasCustomerNumberConflict,
    adminMergePersons,
  ]);

  if (!person || !candidates || candidates.length === 0) {
    return <Loader />;
  }

  return (
    <div className={styles.host}>
      <Frame.SubTitle>Duplikat-Resolver</Frame.SubTitle>

      <Frame.ActionBar>
        <Button
          label={'Zusammenführen'}
          disabled={!mergePossible || !customerNumberResolved || merging}
          primary
          onClick={handleMerge}
        />
      </Frame.ActionBar>

      {customerNumberConflict && hasCustomerNumberConflict && (
        <div style={{ padding: '8px 16px', background: '#fff3cd', color: '#856404' }}>
          Beide Personen haben unterschiedliche Kundennummern.
          Bitte wähle die gewünschte Kundennummer aus.
        </div>
      )}

      <Frame.Content>
        <div className={styles.tableWrapper}>
        <table>
          <thead>
            <tr>
              <th></th>
              <th></th>
              {candidates.map((record, idx) => (
                <th className={styles.headerCell} key={idx}>
                  <Checkbox
                    label={'Kein Duplikat'}
                    checked={!activeRecordIds.includes(record.id)}
                    onChange={async (v) => {
                      toggleActiveRecord(record.id, !v);
                      if (v && person && record.id !== person.id) {
                        try {
                          await adminMarkNotDuplicate({
                            variables: { person1Id: person.id, person2Id: record.id },
                          });
                        } catch {}
                      }
                    }}
                    noSpacing
                  />
                </th>
              ))}
            </tr>
            <tr>
              <th></th>
              <th>Ergebnis</th>
              {candidates.map((record, idx) => (
                <th className={styles.headerCell} key={idx}>
                  <Radio
                    label={`Record ${idx + 1}`}
                    checked={
                      record.id === primaryRecordId &&
                      activeRecordIds.includes(record.id)
                    }
                    disabled={!activeRecordIds.includes(record.id)}
                    onChange={(e) => setPrimaryRecordId(e.target.value)}
                    value={record.id}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <AttributesView
              candidates={candidates as MergeCandidate[]}
              mergeState={mergeState}
              activeRecordIds={activeRecordIds}
              primaryRecordId={primaryRecordId}
              userType={candidates.find((c) => c.user?.__typename)?.user?.__typename as any}
              onStateChange={handleStateChange}
              contactPropsState={contactPropsState}
              onContactPropToggle={handleContactPropToggle}
            />
          </tbody>
        </table>
        </div>
      </Frame.Content>
    </div>
  );
};
